/**
 * Q-Net API 데이터 수집 및 관리 시스템
 * 기존 API 설정을 활용하여 대량 자격증 데이터 수집
 */

import { createNewCertificationMapping, getCertificationInfo } from './unifiedCertificationMappings.js';

// Supabase Edge Function을 통한 Q-Net API 프록시 설정
const QNET_API_CONFIG = {
  proxyUrl: 'https://wvznaebdqkkqnxtxnsod.supabase.co/functions/v1/qnet-proxy',
  supabaseApiKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind2em5hZWJkcWtrcW54dHhuc29kIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU0NDQ2MTksImV4cCI6MjA3MTAyMDYxOX0.kkFkLfefIvWh3MCyLt4TpgFamQjLj2GsyKt6kDE2vYg',
  
  // 캐시 설정
  cacheTimeout: 24 * 60 * 60 * 1000, // 24시간
  maxRetries: 3,
  retryDelay: 1000
};

// 메모리 캐시
const cache = new Map();

/**
 * Supabase Edge Function을 통한 Q-Net API 호출
 * @param {string} jobCode - 자격증 종목코드
 * @returns {Promise<Object>} JSON 응답 데이터 (이미 파싱됨)
 */
async function callQnetAPI(jobCode) {
  const url = new URL(QNET_API_CONFIG.proxyUrl);
  url.searchParams.append('jmCd', jobCode);
  
  let lastError;
  
  for (let attempt = 1; attempt <= QNET_API_CONFIG.maxRetries; attempt++) {
    try {
      console.log(`🔍 Supabase Edge Function 호출 중... 자격증코드: ${jobCode} (시도: ${attempt})`);
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${QNET_API_CONFIG.supabaseApiKey}`,
          'apikey': QNET_API_CONFIG.supabaseApiKey,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const jsonData = await response.json();
      console.log(`✅ Edge Function 응답 수신 완료:`, jsonData.success ? '성공' : '실패');
      
      return jsonData;
      
    } catch (error) {
      console.error(`❌ Edge Function 호출 실패 (${jobCode}, 시도 ${attempt}):`, error.message);
      lastError = error;
      
      if (attempt < QNET_API_CONFIG.maxRetries) {
        await new Promise(resolve => setTimeout(resolve, QNET_API_CONFIG.retryDelay * attempt));
      }
    }
  }
  
  throw lastError;
}

/**
 * XML 응답을 파싱하여 구조화된 데이터 추출
 * @param {string} xmlData - XML 응답 데이터
 * @param {string} jobCode - 자격증 코드
 * @returns {Object} 파싱된 자격증 정보
 */
function parseQnetXmlResponse(xmlData, jobCode) {
  try {
    // 브라우저 환경에서 DOMParser 사용 시도
    if (typeof window !== 'undefined' && window.DOMParser) {
      const parser = new window.DOMParser();
      const xmlDoc = parser.parseFromString(xmlData, 'text/xml');
      return parseXmlDocument(xmlDoc, jobCode);
    }
    
    // Node.js 환경이거나 파싱 실패 시 정규식 사용
    return parseXmlWithRegex(xmlData, jobCode);
    
  } catch (error) {
    console.error(`XML 파싱 실패 (${jobCode}):`, error.message);
    return parseXmlWithRegex(xmlData, jobCode); // 폴백
  }
}

/**
 * DOMParser로 파싱된 XML 문서에서 데이터 추출
 * @param {Document} xmlDoc - 파싱된 XML 문서
 * @param {string} jobCode - 자격증 코드
 * @returns {Object} 파싱된 정보
 */
function parseXmlDocument(xmlDoc, jobCode) {
  try {
    const jmfldnmElement = xmlDoc.querySelector('jmfldnm');
    const contentsElement = xmlDoc.querySelector('contents');
    
    const jmfldnm = jmfldnmElement ? jmfldnmElement.textContent : null;
    const contents = contentsElement ? contentsElement.textContent : null;
    
    return {
      jobCode: jobCode,
      koreanName: jmfldnm,
      contents: contents,
      eligibilityRequirements: parseEligibilityFromContents(contents),
      examSchedule: null, // 별도 API에서 수집
      passRate: null, // 별도 API에서 수집
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error(`XML 문서 파싱 실패 (${jobCode}):`, error.message);
    return null;
  }
}

/**
 * 정규식을 사용한 XML 파싱
 * @param {string} xmlData - XML 데이터
 * @param {string} jobCode - 자격증 코드
 * @returns {Object} 파싱된 정보
 */
function parseXmlWithRegex(xmlData, jobCode) {
  const extractTag = (tagName) => {
    const regex = new RegExp(`<${tagName}[^>]*>(.*?)</${tagName}>`, 'gi');
    const matches = xmlData.match(regex);
    if (matches && matches.length > 0) {
      return matches[0].replace(/<[^>]*>/g, '').trim();
    }
    return null;
  };
  
  const jmfldnm = extractTag('jmfldnm'); // 종목명
  const contents = extractTag('contents'); // 내용 (자격요건 등)
  
  return {
    jobCode: jobCode,
    koreanName: jmfldnm,
    contents: contents,
    eligibilityRequirements: parseEligibilityFromContents(contents),
    examSchedule: null, // 별도 API에서 수집
    passRate: null, // 별도 API에서 수집
    lastUpdated: new Date().toISOString()
  };
}

/**
 * Contents에서 자격요건 파싱
 * @param {string} contents - contents 필드 내용
 * @returns {Array} 자격요건 배열
 */
function parseEligibilityFromContents(contents) {
  if (!contents) return [];
  
  const requirements = [];
  
  // 일반적인 자격요건 패턴들
  const patterns = [
    /학력.*?[:\.]([^\.]+)/gi,
    /경력.*?[:\.]([^\.]+)/gi,
    /자격.*?[:\.]([^\.]+)/gi,
    /응시.*?[:\.]([^\.]+)/gi
  ];
  
  patterns.forEach(pattern => {
    const matches = contents.match(pattern);
    if (matches) {
      matches.forEach(match => {
        const cleaned = match.replace(/[<>]/g, '').trim();
        if (cleaned && !requirements.includes(cleaned)) {
          requirements.push(cleaned);
        }
      });
    }
  });
  
  return requirements;
}

/**
 * 캐시된 데이터 조회
 * @param {string} jobCode - 자격증 코드
 * @returns {Object|null} 캐시된 데이터
 */
function getCachedData(jobCode) {
  const cached = cache.get(jobCode);
  if (cached && Date.now() - cached.timestamp < QNET_API_CONFIG.cacheTimeout) {
    return cached.data;
  }
  return null;
}

/**
 * 데이터 캐시 저장
 * @param {string} jobCode - 자격증 코드
 * @param {Object} data - 저장할 데이터
 */
function setCachedData(jobCode, data) {
  cache.set(jobCode, {
    data,
    timestamp: Date.now()
  });
}

/**
 * 단일 자격증 데이터 가져오기
 * @param {string} jobCode - Q-Net 자격증 코드
 * @returns {Promise<Object>} 자격증 정보
 */
export async function fetchCertificationData(jobCode) {
  try {
    // 캐시 확인
    const cached = getCachedData(jobCode);
    if (cached) {
      console.log(`📦 캐시에서 데이터 반환: ${jobCode}`);
      return cached;
    }
    
    // Supabase Edge Function 호출 (JSON 데이터 직접 반환)
    const jsonData = await callQnetAPI(jobCode);
    
    if (jsonData && jsonData.success) {
      // 통합 매핑 시스템과 연동
      const mappingInfo = getCertificationInfo(jsonData.koreanName);
      
      const enrichedData = {
        ...jsonData,
        urlSlug: mappingInfo?.urlSlug || null,
        officialEnglish: mappingInfo?.english || null,
        hasCBT: mappingInfo?.hasCBT || false,
        protected: mappingInfo?.protected || false
      };
      
      // 캐시 저장
      setCachedData(jobCode, enrichedData);
      
      return enrichedData;
    }
    
    return null;
    
  } catch (error) {
    console.error(`데이터 가져오기 실패 (${jobCode}):`, error.message);
    return null;
  }
}

/**
 * 여러 자격증 데이터 일괄 가져오기
 * @param {Array<string>} jobCodes - 자격증 코드 배열
 * @param {Object} options - 옵션
 * @returns {Promise<Array<Object>>} 자격증 정보 배열
 */
export async function fetchMultipleCertifications(jobCodes, options = {}) {
  const {
    batchSize = 5, // 동시 요청 수
    delay = 1000 // 배치 간 딜레이
  } = options;
  
  const results = [];
  const errors = [];
  
  console.log(`📊 ${jobCodes.length}개 자격증 데이터 수집 시작`);
  
  for (let i = 0; i < jobCodes.length; i += batchSize) {
    const batch = jobCodes.slice(i, i + batchSize);
    
    console.log(`🔄 배치 ${Math.floor(i/batchSize) + 1}: ${batch.join(', ')}`);
    
    const batchPromises = batch.map(async (jobCode) => {
      try {
        const data = await fetchCertificationData(jobCode);
        return { jobCode, data, success: true };
      } catch (error) {
        return { jobCode, error: error.message, success: false };
      }
    });
    
    const batchResults = await Promise.allSettled(batchPromises);
    
    batchResults.forEach(result => {
      if (result.status === 'fulfilled') {
        if (result.value.success) {
          results.push(result.value.data);
        } else {
          errors.push(result.value);
        }
      } else {
        errors.push({ 
          jobCode: 'unknown', 
          error: result.reason?.message || 'Unknown error',
          success: false 
        });
      }
    });
    
    // 다음 배치 전 딜레이
    if (i + batchSize < jobCodes.length) {
      console.log(`⏳ ${delay}ms 대기 중...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  console.log(`✅ 데이터 수집 완료: ${results.length}개 성공, ${errors.length}개 실패`);
  
  if (errors.length > 0) {
    console.warn('실패한 자격증들:', errors.map(e => e.jobCode).join(', '));
  }
  
  return {
    results,
    errors,
    totalRequested: jobCodes.length,
    totalSuccess: results.length,
    totalFailed: errors.length
  };
}

/**
 * 알려진 자격증 코드들로 초기 데이터 수집
 * @returns {Promise<Object>} 수집 결과
 */
export async function initializeKnownCertifications() {
  // qnet-complete-exam-specs.json에서 알려진 코드들 가져오기
  const knownCodes = [
    '1431', // 산업안전기사
    '7910', // 한식조리기능사  
    '1320', // 정보처리기사
    '7780', // 전기기능사
    '7875', // 지게차운전기능사
    '7862', // 굴착기운전기능사
    '1441', // 산업위생관리기사
    '1150', // 전기기사
    '2140', // 전기산업기사
    '1160', // 전기공사기사
    '7632', // 산림기능사
    '1630', // 건축기사
    '1900', // 소방설비기사(기계분야)
    '1910', // 소방설비기사(전기분야)
    '2590', // 공조냉동기계산업기사
    '2121', // 위험물산업기사
    '2670', // 컴퓨터활용능력1급
    '2671'  // 컴퓨터활용능력2급
  ];
  
  return await fetchMultipleCertifications(knownCodes);
}

/**
 * 캐시 통계 정보
 * @returns {Object} 캐시 정보
 */
export function getCacheStats() {
  return {
    totalCached: cache.size,
    cacheEntries: Array.from(cache.keys()),
    cacheTimeout: QNET_API_CONFIG.cacheTimeout
  };
}

/**
 * 캐시 초기화
 */
export function clearCache() {
  cache.clear();
  console.log('🗑️ API 캐시 초기화 완료');
}

// 기본 export
const qnetApiData = {
  fetchCertificationData,
  fetchMultipleCertifications,
  initializeKnownCertifications,
  getCacheStats,
  clearCache
};

export default qnetApiData;